import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Shield as OwnerIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  ProjectCollaborator,
  researchApi,
  ResearchProject,
} from "../../services/researchApi";
import LogoLoader from "../common/LogoLoader";
import { OrganizationMember, settingsApi } from "../../services/settingsApi";

type PermissionLevel = "EDITOR" | "REQ_EDITOR" | "VIEWER";

interface SharingEntry {
  email: string;
  permissionLevel: PermissionLevel;
  isExternal: boolean;
  displayName: string;
}

interface ShareProjectDialogProps {
  open: boolean;
  onClose: () => void;
  project: ResearchProject;
  onShareUpdated: () => void;
}

const PermissionSelect: React.FC<{
  value: PermissionLevel;
  onChange: (v: PermissionLevel) => void;
  size?: "small";
}> = ({ value, onChange, size = "small" }) => (
  <Select
    size={size}
    value={value}
    onChange={(e) => onChange(e.target.value as PermissionLevel)}
    sx={{ minWidth: 170, height: 40 }}
  >
    <MenuItem value="EDITOR">Editor</MenuItem>
    <MenuItem value="REQ_EDITOR">Requirements Editor</MenuItem>
    <MenuItem value="VIEWER">Viewer</MenuItem>
  </Select>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ShareProjectDialog: React.FC<ShareProjectDialogProps> = ({
  open,
  onClose,
  project,
  onShareUpdated,
}) => {
  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);

  // Internal tab state
  const [selectedMember, setSelectedMember] =
    useState<OrganizationMember | null>(null);
  const [internalPermission, setInternalPermission] =
    useState<PermissionLevel>("EDITOR");

  // External tab state
  const [externalEmail, setExternalEmail] = useState("");
  const [externalPermission, setExternalPermission] =
    useState<PermissionLevel>("VIEWER");
  const [externalEmailError, setExternalEmailError] = useState("");

  // Working list of non-owner entries
  const [entries, setEntries] = useState<SharingEntry[]>([]);
  const [dirty, setDirty] = useState(false);

  const ownerId = Number(project.created_by);

  useEffect(() => {
    if (open) {
      loadData();
      setTab(0);
    }
  }, [open, project.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orgData, collabs]: [
        Awaited<ReturnType<typeof settingsApi.getOrganization>>,
        ProjectCollaborator[],
      ] = await Promise.all([
        settingsApi.getOrganization(),
        researchApi.getCollaborators(project.id),
      ]);

      const members = orgData.members.filter(
        (m) => m.account_status !== "DEACTIVATED",
      );
      setOrgMembers(members);

      const memberEmailSet = new Set(members.map((m) => m.email));
      const initialEntries: SharingEntry[] = collabs
        .filter((c) => c.permission_level !== "OWNER")
        .map((c) => ({
          email: c.email,
          permissionLevel: c.permission_level as PermissionLevel,
          isExternal: !memberEmailSet.has(c.email),
          displayName:
            [c.first_name, c.last_name].filter(Boolean).join(" ") ||
            c.username,
        }));

      setEntries(initialEntries);
      setDirty(false);
    } catch (error) {
      console.error("Failed to load sharing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, value: number) => {
    setTab(value);
    setSelectedMember(null);
    setExternalEmail("");
    setExternalEmailError("");
  };

  const handleAddInternal = () => {
    if (!selectedMember) return;
    if (entries.some((e) => e.email === selectedMember.email)) return;
    const displayName =
      [selectedMember.first_name, selectedMember.last_name]
        .filter(Boolean)
        .join(" ") || selectedMember.username;
    setEntries((prev) => [
      ...prev,
      {
        email: selectedMember.email,
        permissionLevel: internalPermission,
        isExternal: false,
        displayName,
      },
    ]);
    setSelectedMember(null);
    setDirty(true);
  };

  const handleAddExternal = () => {
    const email = externalEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setExternalEmailError("Please enter a valid email address.");
      return;
    }
    if (entries.some((e) => e.email === email)) {
      setExternalEmailError("This email is already in the list.");
      return;
    }
    setEntries((prev) => [
      ...prev,
      {
        email,
        permissionLevel: externalPermission,
        isExternal: true,
        displayName: email,
      },
    ]);
    setExternalEmail("");
    setExternalEmailError("");
    setDirty(true);
  };

  const handleRemoveEntry = (email: string) => {
    setEntries((prev) => prev.filter((e) => e.email !== email));
    setDirty(true);
  };

  const handlePermissionChange = (email: string, level: PermissionLevel) => {
    setEntries((prev) =>
      prev.map((e) => (e.email === email ? { ...e, permissionLevel: level } : e)),
    );
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await researchApi.shareProject(
        project.id,
        entries.map((e) => ({
          email: e.email,
          permission_level: e.permissionLevel,
          ...(e.isExternal ? { confirm_external: "true" as const } : {}),
        })),
      );
      onShareUpdated();
      onClose();
    } catch (error) {
      console.error("Failed to update sharing:", error);
    } finally {
      setSaving(false);
    }
  };

  const entryEmails = new Set(entries.map((e) => e.email));
  const availableMembers = orgMembers.filter(
    (m) => m.id !== ownerId && !entryEmails.has(m.email),
  );
  const ownerMember = orgMembers.find((m) => m.id === ownerId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1e1e1e" } }}
    >
      <DialogTitle sx={{ backgroundColor: "#1e1e1e" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Share Project</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {project.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#1e1e1e" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <LogoLoader size="section" surface="paper" label="Loading the members" />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            {/* Add user section */}
            <Box>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
              >
                <Tab label="Organization" />
                <Tab label="External" />
              </Tabs>

              {tab === 0 && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <Autocomplete
                    size="small"
                    sx={{ flex: 1 }}
                    options={availableMembers}
                    value={selectedMember}
                    onChange={(_, value) => setSelectedMember(value)}
                    getOptionLabel={(option) => {
                      const name = [option.first_name, option.last_name]
                        .filter(Boolean)
                        .join(" ");
                      return name ? `${name} (${option.email})` : option.email;
                    }}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">
                            {[option.first_name, option.last_name]
                              .filter(Boolean)
                              .join(" ") || option.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.email}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search members..."
                        variant="outlined"
                      />
                    )}
                    noOptionsText="No members available"
                  />
                  <PermissionSelect
                    value={internalPermission}
                    onChange={setInternalPermission}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={handleAddInternal}
                    disabled={!selectedMember}
                    sx={{ height: 40, whiteSpace: "nowrap" }}
                  >
                    Add
                  </Button>
                </Box>
              )}

              {tab === 1 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Alert severity="warning" sx={{ fontSize: "0.8rem" }}>
                    If this email belongs to an existing account, they will be
                    granted access to this project.
                  </Alert>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <TextField
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Email address"
                      value={externalEmail}
                      onChange={(e) => {
                        setExternalEmail(e.target.value);
                        if (externalEmailError) setExternalEmailError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddExternal();
                      }}
                      error={!!externalEmailError}
                      helperText={externalEmailError}
                      variant="outlined"
                    />
                    <PermissionSelect
                      value={externalPermission}
                      onChange={setExternalPermission}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<PersonAddIcon />}
                      onClick={handleAddExternal}
                      sx={{ height: 40, whiteSpace: "nowrap" }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            {/* People with access */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                People with access
              </Typography>

              {/* Owner row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  mb: 0.5,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">
                    {ownerMember
                      ? [ownerMember.first_name, ownerMember.last_name]
                          .filter(Boolean)
                          .join(" ") || ownerMember.username
                      : project.created_by_username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ownerMember?.email ?? ""}
                  </Typography>
                </Box>
                <Chip
                  icon={<OwnerIcon sx={{ fontSize: 14 }} />}
                  label="Owner"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: "0.75rem" }}
                />
              </Box>

              {/* Shared entries */}
              {entries.map((entry) => (
                <Box
                  key={entry.email}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {entry.displayName !== entry.email && (
                        <Typography variant="body2" noWrap>
                          {entry.displayName}
                        </Typography>
                      )}
                      {entry.isExternal && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="warning.main"
                        >
                          (external)
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {entry.email}
                    </Typography>
                  </Box>
                  <PermissionSelect
                    value={entry.permissionLevel}
                    onChange={(level) =>
                      handlePermissionChange(entry.email, level)
                    }
                  />
                  <Tooltip title="Remove access">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveEntry(entry.email)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}

              {entries.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1, px: 1.5, fontStyle: "italic" }}
                >
                  Only you have access to this project.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#1e1e1e", px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareProjectDialog;
